import LiquidText, { liquidTone } from '../components/LiquidText';
import { LiquidDialog } from '../components/LiquidUI';
import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { abrirOuDescarregar } from '../utils/abrirFicheiro';

/* ============================================================
   GESTÃO DE INSTALAÇÕES (P_IM_01)
   Instalações da SPROD e respetivos Registos de Segurança /
   manutenção (extintores, CDI, iluminação de emergência, AVAC,
   limpeza, manutenção geral…), com anexos.
   ============================================================ */

const TIPO_INST = { edificio: 'Edifício', armazem: 'Armazém', escritorio: 'Escritório', estaleiro: 'Estaleiro', outro: 'Outro' };

const CATEGORIAS_REG = [
    { v: 'extintor', l: 'Extintores', icon: '🧯' },
    { v: 'cdi', l: 'Central Deteção Incêndios (CDI)', icon: '🚨' },
    { v: 'iluminacao_emergencia', l: 'Iluminação de emergência', icon: '💡' },
    { v: 'porta_corta_fogo', l: 'Porta corta-fogo', icon: '🚪' },
    { v: 'avac', l: 'AVAC', icon: '❄️' },
    { v: 'limpeza', l: 'Limpeza / higienização', icon: '🧽' },
    { v: 'manutencao_geral', l: 'Manutenção geral', icon: '🔧' },
    { v: 'outro', l: 'Outro', icon: '📋' },
];
const CATEGORIA_REG_INFO = Object.fromEntries(CATEGORIAS_REG.map(c => [c.v, c]));

const RESULTADO_INFO = {
    conforme: { label: 'Conforme', bg: '#dcfce7', fg: '#166534' },
    nao_conforme: { label: 'Não conforme', bg: '#fee2e2', fg: '#991b1b' },
    pendente: { label: 'Pendente', bg: '#f1f5f9', fg: '#475569' },
};
const PERIODICIDADES = ['mensal', 'trimestral', 'semestral', 'anual', 'bianual', 'outro'];
const ANEXO_CATS = [
    { v: 'relatorio', l: 'Relatório', icon: '📄' },
    { v: 'certificado', l: 'Certificado', icon: '📜' },
    { v: 'evidencia', l: 'Evidências', icon: '📎' },
];

const toDateInput = (v) => {
    if (!v) return '';
    if (typeof v === 'string') return v.slice(0, 10);
    try { const d = new Date(v); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); }
    catch { return ''; }
};
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('pt-PT') : '—');
const isOverdue = (v) => {
    if (!v) return false;
    const d = new Date(v); d.setHours(0, 0, 0, 0);
    const h = new Date(); h.setHours(0, 0, 0, 0);
    return d < h;
};
const fmtBytes = (n) => {
    const b = parseInt(n, 10) || 0;
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

/* ---------- Anexos de um registo ---------- */
function AnexoBloco({ registoId, categoria, label, icon, anexos, onChanged }) {
    const [busy, setBusy] = useState(false);
    const lista = anexos.filter(a => a.categoria === categoria);

    const carregar = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (!files.length) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append('categoria', categoria);
            files.forEach(f => fd.append('files', f));
            await api.post(`/instalacoes/registos/${registoId}/anexos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            onChanged();
        } catch { alert('Erro ao carregar ficheiro'); } finally { setBusy(false); }
    };
    const baixar = async (a) => {
        try {
            const res = await api.get(`/instalacoes/anexos/${a.id}`, { responseType: 'blob' });
            abrirOuDescarregar(res, a.nome_original || 'anexo');
        } catch { alert('Erro ao descarregar'); }
    };
    const remover = async (a) => {
        if (!window.confirm(`Remover "${a.nome_original}"?`)) return;
        try { await api.delete(`/instalacoes/anexos/${a.id}`); onChanged(); } catch { alert('Erro ao remover'); }
    };

    return (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, background: '#f8fafc' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <strong style={{ fontSize: '0.82rem' }}><LiquidText>{icon}</LiquidText> <LiquidText>{label}</LiquidText></strong>
                <label style={{ ...btnSec, padding: '4px 8px', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
                    {busy ? '…' : '＋'}
                    <input type="file" multiple onChange={carregar} disabled={busy} style={{ display: 'none' }} />
                </label>
            </div>
            {lista.length === 0
                ? <div style={{ fontSize: '0.75rem', color: '#cbd5e1' }}>Sem ficheiros.</div>
                : lista.map(a => (
                    <div data-liquid-actions="true" key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem' }}>
                        <button onClick={() => baixar(a)} style={{ ...linkBtn, flex: 1, textAlign: 'left' }}><LiquidText>📄 </LiquidText>{a.nome_original}</button>
                        <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>{fmtBytes(a.tamanho)}</span>
                        <button onClick={() => remover(a)} style={{ ...linkBtn, color: '#dc2626' }} aria-label="Apagar" title="Apagar"><DeleteIcon /></button>
                    </div>
                ))}
        </div>
    );
}

/* ---------- Modal de registo de segurança ---------- */
function ModalRegisto({ instalacaoId, registo, onClose, onSaved }) {
    const novo = !registo?.id;
    const [savedId, setSavedId] = useState(registo?.id || null);
    const [anexos, setAnexos] = useState(registo?.anexos || []);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const [form, setForm] = useState({
        categoria: registo?.categoria || 'extintor',
        descricao: registo?.descricao || '',
        periodicidade: registo?.periodicidade || '',
        ultima_verificacao: toDateInput(registo?.ultima_verificacao),
        proxima_verificacao: toDateInput(registo?.proxima_verificacao),
        resultado: registo?.resultado || 'pendente',
        responsavel: registo?.responsavel || '',
        entidade_externa: registo?.entidade_externa || '',
        observacoes: registo?.observacoes || '',
        // Diligência em curso: o que já foi feito para repor a verificação em dia.
        acao_descricao: registo?.acao_descricao || '',
        acao_data: toDateInput(registo?.acao_data),
        acao_prevista: toDateInput(registo?.acao_prevista),
    });
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const recarregarAnexos = useCallback(async () => {
        if (!savedId) return;
        try {
            const r = await api.get(`/instalacoes/${instalacaoId}`);
            const rg = (r.data.registos || []).find(x => x.id === savedId);
            setAnexos(rg?.anexos || []);
        } catch { /* ignora */ }
    }, [savedId, instalacaoId]);

    const guardar = async () => {
        setSaving(true); setErr('');
        try {
            if (savedId) {
                await api.put(`/instalacoes/registos/${savedId}`, form);
                onSaved(); onClose();
            } else {
                const res = await api.post(`/instalacoes/${instalacaoId}/registos`, form);
                setSavedId(res.data.id);
                onSaved();
            }
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao guardar'); } finally { setSaving(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 620 }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <h3 style={{ margin: 0 }}>{novo && !savedId ? 'Novo registo de segurança' : 'Registo de segurança'}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>

                <div style={row2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Categoria</label>
                        <select value={form.categoria} onChange={e => set('categoria', e.target.value)} style={{ ...inp, width: '100%' }}>
                            {CATEGORIAS_REG.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
                        </select>
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Periodicidade</label>
                        <select value={form.periodicidade} onChange={e => set('periodicidade', e.target.value)} style={{ ...inp, width: '100%' }}>
                            <option value="">—</option>
                            {PERIODICIDADES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                        </select>
                    </div>
                </div>

                <div style={{ marginTop: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Descrição</label>
                    <input value={form.descricao} onChange={e => set('descricao', e.target.value)} placeholder="Ex.: Extintor pó químico 6kg — receção" style={{ ...inp, width: '100%' }} />
                </div>

                <div style={row3}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Última verificação</label>
                        <input type="date" value={form.ultima_verificacao} onChange={e => set('ultima_verificacao', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Próxima verificação</label>
                        <input type="date" value={form.proxima_verificacao} onChange={e => set('proxima_verificacao', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Resultado</label>
                        <select value={form.resultado} onChange={e => set('resultado', e.target.value)} style={{ ...inp, width: '100%' }}>
                            {Object.entries(RESULTADO_INFO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                    </div>
                </div>

                <div style={row2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Responsável</label>
                        <input value={form.responsavel} onChange={e => set('responsavel', e.target.value)} placeholder="RQS / TAM…" style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Entidade externa</label>
                        <input value={form.entidade_externa} onChange={e => set('entidade_externa', e.target.value)} placeholder="Prestador (se aplicável)" style={{ ...inp, width: '100%' }} />
                    </div>
                </div>

                {/* Ação em curso — para uma verificação vencida poder mostrar a
                    diligência já feita, com o email ou o orçamento anexados em
                    "evidência". O prazo continua a contar; o que muda é ficar
                    documentado o que está a ser feito. */}
                <div style={{ marginTop: 14, padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                    <strong style={{ fontSize: '0.86rem', color: '#334155' }}>Ação em curso</strong>
                    <p style={{ margin: '2px 0 10px', fontSize: '0.75rem', color: '#94a3b8' }}>
                        Se a verificação está vencida mas já houve diligência — pedido de agendamento,
                        orçamento pedido —, registe-a aqui e anexe o comprovativo em «evidência».
                    </p>
                    <div style={{ marginBottom: 10 }}>
                        <label data-liquid-label="true" style={lbl}>O que foi feito</label>
                        <input value={form.acao_descricao} onChange={e => set('acao_descricao', e.target.value)}
                            placeholder="ex.: pedido de agendamento enviado à PRO-FIRE" style={{ ...inp, width: '100%' }} />
                    </div>
                    <div style={row2}>
                        <div>
                            <label data-liquid-label="true" style={lbl}>Data da diligência</label>
                            <input type="date" value={form.acao_data} onChange={e => set('acao_data', e.target.value)} style={{ ...inp, width: '100%' }} />
                        </div>
                        <div>
                            <label data-liquid-label="true" style={lbl}>Intervenção prevista para</label>
                            <input type="date" value={form.acao_prevista} onChange={e => set('acao_prevista', e.target.value)} style={{ ...inp, width: '100%' }} />
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Observações</label>
                    <textarea value={form.observacoes} rows={2} onChange={e => set('observacoes', e.target.value)} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>

                {/* Anexos */}
                <div style={{ marginTop: 16 }}>
                    <strong style={{ fontSize: '0.9rem' }}>Ficheiros</strong>
                    {!savedId ? (
                        <div data-liquid-notice="true" data-liquid-tone={liquidTone('#fffbeb')} style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: '8px 12px', borderRadius: 8, fontSize: '0.8rem', marginTop: 6 }}>
                            Guarde o registo para anexar relatórios/certificados.
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginTop: 6 }}>
                            {ANEXO_CATS.map(c => (
                                <AnexoBloco key={c.v} registoId={savedId} categoria={c.v} label={c.l} icon={c.icon} anexos={anexos} onChanged={recarregarAnexos} />
                            ))}
                        </div>
                    )}
                </div>

                {err && <div style={{ color: '#e53e3e', marginTop: 10, fontSize: '0.85rem' }}>{err}</div>}
                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>{savedId && novo ? 'Concluído' : 'Cancelar'}</button>
                    <button data-liquid-primary="true" onClick={guardar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>
                        {saving ? 'A guardar…' : (savedId ? 'Guardar alterações' : 'Guardar registo')}
                    </button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Modal de instalação (dados) ---------- */
function ModalInstalacao({ instalacao, onClose, onSaved }) {
    const [form, setForm] = useState({
        nome: instalacao?.nome || '',
        tipo: instalacao?.tipo || 'edificio',
        morada: instalacao?.morada || '',
        arrendada: instalacao?.arrendada ?? true,
        senhorio: instalacao?.senhorio || '',
        responsavel: instalacao?.responsavel || '',
        observacoes: instalacao?.observacoes || '',
    });
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    const guardar = async () => {
        if (!form.nome.trim()) { setErr('O nome é obrigatório.'); return; }
        setSaving(true); setErr('');
        try {
            if (instalacao?.id) await api.put(`/instalacoes/${instalacao.id}`, form);
            else await api.post('/instalacoes', form);
            onSaved(); onClose();
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao guardar'); } finally { setSaving(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <h3 style={{ margin: 0 }}>{instalacao?.id ? 'Editar instalação' : 'Nova instalação'}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>

                <div style={row2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Nome</label>
                        <input value={form.nome} onChange={e => set('nome', e.target.value)} placeholder="Ex.: Sede / Armazém" style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Tipo</label>
                        <select value={form.tipo} onChange={e => set('tipo', e.target.value)} style={{ ...inp, width: '100%' }}>
                            {Object.entries(TIPO_INST).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </div>
                </div>

                <div style={{ marginTop: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Morada</label>
                    <input value={form.morada} onChange={e => set('morada', e.target.value)} style={{ ...inp, width: '100%' }} />
                </div>

                <div style={{ ...row3, alignItems: 'end' }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Arrendada</label>
                        <div data-liquid-actions="true" style={{ display: 'flex', gap: 6 }}>
                            <button type="button" onClick={() => set('arrendada', true)}
                                style={{ ...toggle, ...(form.arrendada ? toggleOn : {}) }}>Sim</button>
                            <button type="button" onClick={() => set('arrendada', false)}
                                style={{ ...toggle, ...(!form.arrendada ? toggleOn : {}) }}>Não</button>
                        </div>
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Senhorio</label>
                        <input value={form.senhorio} onChange={e => set('senhorio', e.target.value)} disabled={!form.arrendada}
                            style={{ ...inp, width: '100%', opacity: form.arrendada ? 1 : 0.5 }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Responsável</label>
                        <input value={form.responsavel} onChange={e => set('responsavel', e.target.value)} placeholder="RQS…" style={{ ...inp, width: '100%' }} />
                    </div>
                </div>

                <div style={{ marginTop: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Observações</label>
                    <textarea value={form.observacoes} rows={2} onChange={e => set('observacoes', e.target.value)} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>

                {err && <div style={{ color: '#e53e3e', marginTop: 10, fontSize: '0.85rem' }}>{err}</div>}
                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-primary="true" onClick={guardar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>
                        {saving ? 'A guardar…' : 'Guardar'}
                    </button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Modal de detalhe (instalação + registos) ---------- */
// `embutido` — mostra o detalhe direto na página, sem janela por cima. É assim
// que aparece no separador Instalações: os registos de segurança ficam à vista
// sem ser preciso abrir nada.
function ModalDetalhe({ instalacaoId, onClose, onChanged, embutido = false }) {
    const [inst, setInst] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editInst, setEditInst] = useState(false);
    const [registoModal, setRegistoModal] = useState(null); // {} novo | obj edit

    const carregar = useCallback(async () => {
        setLoading(true);
        try { const r = await api.get(`/instalacoes/${instalacaoId}`); setInst(r.data); }
        catch { setInst(null); } finally { setLoading(false); }
    }, [instalacaoId]);

    useEffect(() => { carregar(); }, [carregar]);

    const eliminarRegisto = async (rg) => {
        if (!window.confirm('Eliminar este registo e os seus ficheiros?')) return;
        try { await api.delete(`/instalacoes/registos/${rg.id}`); carregar(); onChanged?.(); }
        catch { alert('Erro ao eliminar'); }
    };

    // Nada de definir o invólucro como componente aqui dentro: o React remontava
    // a árvore toda a cada render e os campos perdiam o foco a meio da escrita.
    const conteudo = (
        <>
            {loading || !inst ? (
                <p style={{ color: '#64748b' }}>A carregar…</p>
            ) : (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                            <div>
                                <h3 style={{ margin: 0 }}>{inst.nome}</h3>
                                <div style={{ color: '#64748b', fontSize: '0.85rem', marginTop: 2 }}>
                                    {TIPO_INST[inst.tipo] || inst.tipo}{inst.morada ? ` · ${inst.morada}` : ''}
                                    {inst.arrendada ? ` · Arrendada${inst.senhorio ? ` (${inst.senhorio})` : ''}` : ' · Própria'}
                                </div>
                            </div>
                            {!embutido && <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>}
                        </div>

                        <div data-liquid-actions="true" style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                            <button data-liquid-secondary="true" onClick={() => setEditInst(true)} style={btnSec}><LiquidText>✏️ Editar instalação</LiquidText></button>
                            <button data-liquid-primary="true" onClick={() => setRegistoModal({})} style={{ ...btnPri, marginLeft: 'auto' }}>＋ Novo registo de segurança</button>
                        </div>

                        {inst.registos.length === 0 ? (
                            <div style={{ padding: '30px 0', textAlign: 'center', color: '#94a3b8' }}>
                                Sem registos de segurança. Adicione extintores, CDI, iluminação de emergência, etc.
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="standard-table">
                                    <thead>
                                        <tr>
                                            <th>Categoria</th>
                                            <th>Descrição</th>
                                            <th>Periodic.</th>
                                            <th>Última</th>
                                            <th>Próxima</th>
                                            <th style={{ textAlign: 'center' }}>Resultado</th>
                                            <th style={{ textAlign: 'center' }}>Anexos</th>
                                            <th style={{ textAlign: 'right' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {inst.registos.map(rg => {
                                            const ci = CATEGORIA_REG_INFO[rg.categoria] || CATEGORIA_REG_INFO.outro;
                                            const ri = RESULTADO_INFO[rg.resultado] || RESULTADO_INFO.pendente;
                                            const over = isOverdue(rg.proxima_verificacao);
                                            return (
                                                <tr key={rg.id}>
                                                    <td style={{ whiteSpace: 'nowrap' }}><LiquidText>{ci.icon}</LiquidText> {ci.l}</td>
                                                    <td>{rg.descricao || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                                                    <td style={{ textTransform: 'capitalize' }}>{rg.periodicidade || '—'}</td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(rg.ultima_verificacao)}</td>
                                                    <td style={{ whiteSpace: 'nowrap', color: over ? '#dc2626' : 'inherit', fontWeight: over ? 700 : 400 }}>
                                                        {fmtDate(rg.proxima_verificacao)}<LiquidText>{over ? ' ⚠' : ''}</LiquidText>
                                                        {/* Vencida mas com diligência registada: continua vencida,
                                                            deixa é de parecer esquecida. */}
                                                        {over && rg.acao_descricao && (
                                                            <div data-liquid-notice="true" data-liquid-tone={liquidTone('#fef3c7')} title={rg.acao_descricao} style={{
                                                                marginTop: 3, fontSize: '0.68rem', fontWeight: 600,
                                                                color: '#92400e', background: '#fef3c7',
                                                                borderRadius: 8, padding: '1px 7px', display: 'inline-block',
                                                                maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis',
                                                            }}><LiquidText>
                                                                ⏳ </LiquidText>{rg.acao_descricao}
                                                                {rg.acao_prevista ? ` · prev. ${fmtDate(rg.acao_prevista)}` : ''}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}><span data-liquid-tone={liquidTone(ri.bg)} data-liquid-badge="true" style={{ ...badge, background: ri.bg, color: ri.fg }}><LiquidText>{ri.label}</LiquidText></span></td>
                                                    <td style={{ textAlign: 'center' }}>{rg.anexos?.length ? `📎 ${rg.anexos.length}` : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                                                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                        <button data-liquid-secondary="true" onClick={() => setRegistoModal(rg)} style={{ ...btnSec, marginRight: 6 }}>Abrir</button>
                                                        <button data-liquid-secondary="true" onClick={() => eliminarRegisto(rg)} style={{ ...btnSec, color: '#dc2626', borderColor: '#fecaca' }}><DeleteIcon /> Eliminar</button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}

                {editInst && inst && (
                    <ModalInstalacao instalacao={inst} onClose={() => setEditInst(false)}
                        onSaved={() => { carregar(); onChanged?.(); }} />
                )}
                {registoModal && (
                    <ModalRegisto instalacaoId={instalacaoId} registo={registoModal.id ? registoModal : null}
                        onClose={() => setRegistoModal(null)} onSaved={() => { carregar(); onChanged?.(); }} />
                )}
        </>
    );

    if (embutido) {
        return <div style={{ background: '#fff', borderRadius: 12, padding: 18, border: '1px solid #e2e8f0' }}>{conteudo}</div>;
    }
    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 900 }} onClick={e => e.stopPropagation()}>{conteudo}</div>
        </LiquidDialog>
    );
}

/* ---------- Página ---------- */
const Instalacoes = () => {
    const [lista, setLista] = useState([]);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);
    const [modalInst, setModalInst] = useState(null);
    const [detalheId, setDetalheId] = useState(null);

    const carregar = useCallback(async () => {
        setLoading(true); setErro(null);
        try { const r = await api.get('/instalacoes'); setLista(r.data || []); }
        catch (e) { console.error(e); setErro('Não foi possível carregar as instalações.'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    // Os registos de segurança ficam à vista, sem ser preciso abrir a instalação.
    // Com várias instalações os cartões passam a servir de seletor.
    useEffect(() => {
        if (lista.length && !lista.some(i => i.id === detalheId)) setDetalheId(lista[0].id);
    }, [lista, detalheId]);

    const eliminar = async (inst) => {
        if (!window.confirm(`Eliminar "${inst.nome}" e todos os seus registos/ficheiros?`)) return;
        try { await api.delete(`/instalacoes/${inst.id}`); setDetalheId(null); carregar(); }
        catch (e) { alert(e.response?.data?.error || 'Erro ao eliminar'); }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                <div style={{ color: '#64748b', fontSize: '0.9rem' }}>
                    Instalações da SPROD e respetivos registos de segurança (P_IM_01).
                </div>
                <button data-liquid-primary="true" onClick={() => setModalInst({})} style={btnPri}>＋ Nova instalação</button>
            </div>

            {loading ? (
                <p style={{ color: '#718096' }}>A carregar…</p>
            ) : erro ? (
                <p style={{ color: '#e53e3e' }}>{erro}</p>
            ) : lista.length === 0 ? (
                <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>
                    Nenhuma instalação registada.<br />
                    <span style={{ fontSize: '0.85rem' }}>Use “＋ Nova instalação” para começar.</span>
                </div>
            ) : (
                <div style={{ display: lista.length > 1 ? 'grid' : 'none', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
                    {lista.map(inst => {
                        const over = parseInt(inst.em_atraso, 10) > 0;
                        return (
                            <div key={inst.id} style={{ ...card, outline: inst.id === detalheId ? '2px solid #3182ce' : 'none' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '1.05rem' }}><LiquidText>🏢 </LiquidText>{inst.nome}</h3>
                                        <div style={{ color: '#718096', fontSize: '0.82rem', marginTop: 2 }}>
                                            {TIPO_INST[inst.tipo] || inst.tipo}{inst.arrendada ? ' · Arrendada' : ' · Própria'}
                                        </div>
                                    </div>
                                    {over && <span data-liquid-tone={liquidTone('#fee2e2')} data-liquid-badge="true" style={{ ...badge, background: '#fee2e2', color: '#991b1b' }}>{inst.em_atraso} em atraso</span>}
                                </div>

                                {inst.morada && <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 8 }}><LiquidText>📍 </LiquidText>{inst.morada}</div>}

                                <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: '0.82rem', color: '#475569' }}>
                                    <span><LiquidText>📋 </LiquidText>{inst.num_registos || 0} registo(s)</span>
                                    <span style={{ color: isOverdue(inst.proxima_verificacao) ? '#dc2626' : '#475569' }}><LiquidText>
                                        ⏱ Próx.: </LiquidText>{fmtDate(inst.proxima_verificacao)}
                                    </span>
                                </div>

                                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12, paddingTop: 10, borderTop: '1px solid #edf2f7' }}>
                                    <button data-liquid-primary={(inst.id === detalheId) ? undefined : true} data-liquid-secondary={(inst.id === detalheId) ? true : undefined} onClick={() => setDetalheId(inst.id)} style={inst.id === detalheId ? btnSec : btnPri}>
                                        {inst.id === detalheId ? 'A mostrar' : 'Mostrar'}
                                    </button>
                                    <button data-liquid-secondary="true" onClick={() => setModalInst(inst)} style={btnSec}>Editar</button>
                                    <button data-liquid-secondary="true" onClick={() => eliminar(inst)} style={{ ...btnSec, color: '#dc2626', borderColor: '#fecaca' }}><DeleteIcon /> Eliminar</button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {modalInst && (
                <ModalInstalacao instalacao={modalInst.id ? modalInst : null}
                    onClose={() => setModalInst(null)} onSaved={carregar} />
            )}
            {detalheId && (
                <div style={{ marginTop: lista.length > 1 ? 16 : 0 }}>
                    <ModalDetalhe instalacaoId={detalheId} embutido onChanged={carregar}
                        onClose={() => setDetalheId(null)} />
                </div>
            )}
        </div>
    );
};

/* ---------- estilos ---------- */
const lbl = { display: 'block', fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem' };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const linkBtn = { border: 'none', background: 'transparent', cursor: 'pointer', color: '#2563eb', padding: 0, fontSize: '0.8rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 620, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };
const card = { background: '#fff', borderRadius: 10, padding: 18, boxShadow: '0 2px 5px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' };
const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 };
const row3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12 };
const toggle = { flex: 1, padding: '7px 6px', borderRadius: 8, cursor: 'pointer', border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: '0.85rem' };
const toggleOn = { border: '1px solid #3182ce', background: '#ebf5ff', color: '#1e40af', fontWeight: 600 };

export default Instalacoes;
